import React, { FunctionComponent, useEffect, useState } from 'react';
import { Button, Col, Dropdown, DropdownButton, Row } from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import {
  AuthedLayout,
  EditableTableDiv,
  HeaderDisplayKeysWithType,
} from '../../../components';
import { useClassGrade, useSemGrade } from '../../../hook';
import { Sight, StudentGradeNoSeat, UserInfo } from '../../../model';
import apis from '../../../apis';
import { SightContext } from '../../TableHeaders/SightHeader';
import { downloadDataAsExcel, printDataAsTable, SheetHeaderDisplay } from '../../../utils';
import { sightDiag } from '../../../utils/sight';
import { Nullable } from '../../../types';
import { DateTime } from 'luxon';
import { I18N } from '../../../i18n-raw';
import { toast } from 'react-toastify';

type PageData = StudentGradeNoSeat & Nullable<Sight> & { diag: string };
const formatSightValue = (value: unknown) => {
  if (typeof value !== 'number') return value as string;

  if (value === -1) return '<0.1';
  if (value < -1) return String(value);

  return Number.isInteger(value) ? value.toFixed(1) : String(value);
};
const tableHeader: (SheetHeaderDisplay<PageData> &
  HeaderDisplayKeysWithType<PageData>)[] = [
    { display: '座號', property: 'seat' },
    { display: '姓名', property: 'name' },
    {
      display: '裸視右',
      property: 'sight0R',
      onRender: (value) => formatSightValue(value),
      onSheetRender: (value) => formatSightValue(value),
    },
    {
      display: '裸視左',
      property: 'sight0L',
      onRender: (value) => formatSightValue(value),
      onSheetRender: (value) => formatSightValue(value),
    },
    {
      display: '戴鏡右',
      property: 'sightR',
      onRender: (value) => formatSightValue(value),
      onSheetRender: (value) => formatSightValue(value),
    },
    {
      display: '戴鏡左',
      property: 'sightL',
      onRender: (value) => formatSightValue(value),
      onSheetRender: (value) => formatSightValue(value),
    },
    { display: '近視右', property: 'eNearR' },
    { display: '近視左', property: 'eNearL' },
    { display: '遠視右', property: 'eFarR' },
    { display: '遠視左', property: 'eFarL' },
    { display: '散光右', property: 'eSanR' },
    { display: '散光左', property: 'eSanL' },
    { display: '診斷', property: 'diag' },
    {
      display: '處置代號',
      property: 'sightManageCodes',
      onSheetRender: (v) => (Array.isArray(v) ? v.join(',') : (v as string)),
      onRender: (v) => (Array.isArray(v) ? v.join(',') : (v as string)),
    },
    {
      display: '處置',
      property: 'sightManageCodes',
      onRender: (v) => {
        if (Array.isArray(v)) {
          return v.map(id => I18N.SightManageCodeMap[id] || '').join(', ');
        }
        return '';
      },
      onSheetRender: (v) => {
        if (Array.isArray(v)) {
          return v.map(id => I18N.SightManageCodeMap[id] || '').join(', ');
        }
        return '';
      },
    },
    {
      display: '定期檢查',
      property: 'periodical',
      onSheetRender: (v) => {
        return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : ('' as string);
      },
      onRender: (v) => {
        return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : v;
      },
    },
  ];

const mapState = (app: ApplicationState) => ({ ...app.auth });
const mapDispatch = ErrorDispatches;

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;

const sightClassNotice: FunctionComponent<Props> = ({
  user,
  catchErrorForModal,
}) => {
  const { yearSem, element: semGradeElement } = useSemGrade();
  const [userinfo, setUser] = useState<UserInfo>();
  const { selectedClassGrade: classGrade, element: classGradeElement } =
    useClassGrade({
      yearSem,
    });
  const [students, setStudents] = useState<PageData[]>([]);

  useEffect(() => {
    if (yearSem && classGrade) {
      toast
        .promise(
          apis
            .getStudentsSight(yearSem.year, yearSem.sem, classGrade.id)
            .then(r => {
              setStudents(
                r.map(({ sight, ...s }) => ({
                  ...sight,
                  ...s,
                  diag: sightDiag(sight),
                }))
              )
            }),
          {
            pending: '資料載入中......',
            success: '資料載入成功！',
            error: '資料載入失敗！請查看錯誤資訊。',
          }
        )
        .catch(catchErrorForModal);

      apis.getUserInfo()
        .then((u) => {
          setUser(u);
        })
        .catch(catchErrorForModal);
    }
  }, [classGrade?.id, yearSem?.year, yearSem?.sem]);

  console.log(user)

  return (
    <AuthedLayout>
      <Row className="justify-content-between">
        <Col>
          <Row>
            <Col xs={3} className="mr-3">
              {semGradeElement}
            </Col>
            <Col xs={3} className="mr-3">
              {classGradeElement}
            </Col>
          </Row>
        </Col>
        <div className="d-flex">
          {/* 所有班級 */}
          <DropdownButton
            id="all-class-export"
            title="所有班級"
            variant="info"
            className="mr-3"
            disabled={!yearSem}
          >
            <Dropdown.Item
              onClick={() => {
                if (!yearSem) return;

                apis
                  .getStudentsSight(
                    yearSem.year,
                    yearSem.sem
                  )
                  .then((r) => {
                    const allStudents = r.map(
                      ({ sight, ...s }) => ({
                        ...sight,
                        ...s,
                        diag: sightDiag(sight),
                      })
                    );

                    downloadDataAsExcel({
                      title:
                        `${user.schools[user.currentSchool].name}` +
                        `${yearSem.year}學年` +
                        `${yearSem.sem}學期_班級視力清單`,
                      values: allStudents,
                      headers: tableHeader,
                      context: SightContext,
                      groupBy: (v) =>
                        `${v.grade}年${v.no}班_班級視力清單`,
                      footer:
                        '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                      exportfile: userinfo?.exportFileType,
                    });
                  })
                  .catch(catchErrorForModal);
              }}
            >
              Excel 下載
            </Dropdown.Item>

            <Dropdown.Item
              onClick={() => {
                if (!yearSem) return;

                apis
                  .getStudentsSight(
                    yearSem.year,
                    yearSem.sem
                  )
                  .then((r) => {
                    const allStudents = r.map(
                      ({ sight, ...s }) => ({
                        ...sight,
                        ...s,
                        diag: sightDiag(sight),
                      })
                    );

                    printDataAsTable({
                      title:
                        `${user.schools[user.currentSchool].name}` +
                        `${yearSem.year}學年` +
                        `${yearSem.sem}學期_班級視力清單`,
                      values: allStudents,
                      headers: tableHeader,
                      context: SightContext,
                      groupBy: (v) =>
                        `${v.grade}年${v.no}班_班級視力清單`,
                      footer:
                        '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                    });
                  })
                  .catch(catchErrorForModal);
              }}
            >
              列印
            </Dropdown.Item>
          </DropdownButton>

          {/* 單一班級 */}
          <DropdownButton
            id="single-class-export"
            title="單一班級"
            variant="success"
            disabled={!students.length}
          >
            <Dropdown.Item
              onClick={() => {
                downloadDataAsExcel({
                  title:
                    `${user.schools[user.currentSchool].name}` +
                    `${yearSem?.year}學年` +
                    `${yearSem?.sem}學期_班級視力清單`,
                  values: students,
                  headers: tableHeader,
                  context: SightContext,
                  footer:
                    '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                  exportfile: userinfo?.exportFileType,
                });
              }}
            >
              Excel 下載
            </Dropdown.Item>

            <Dropdown.Item
              onClick={() => {
                printDataAsTable({
                  title:
                    `${user.schools[user.currentSchool].name}` +
                    `${yearSem?.year}學年` +
                    `${yearSem?.sem}學期_班級視力清單`,
                  values: students,
                  headers: tableHeader,
                  context: SightContext,
                  footer:
                    '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                });
              }}
            >
              列印
            </Dropdown.Item>
          </DropdownButton>
        </div>
      </Row>
      <hr />
      <Row>
        <EditableTableDiv
          headers={tableHeader}
          values={students}
          context={SightContext}
        />
      </Row>
    </AuthedLayout>
  );
};

export const SightClassNotice = connector(sightClassNotice);
